import React, { useEffect, useState } from 'react';
import { PlusOutlined, AuditOutlined, UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import { Col, Tag, Select, Space, Popconfirm, message, Modal, Upload } from 'antd';
import { Formik } from 'formik';
import { Form, InputNumber } from 'formik-antd';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import Box from '~/components/Box';
import Row from '~/components/Row';
import Button from '~/components/Button';
import EstimateForm from './form';
import moment from 'moment';
import { paginationEstimates } from '~/services/hooks/estimates';
import { paginationAreas } from '~/services/hooks/areas';
import { paginationLaborTypes } from '~/services/hooks/laborTypes';
import { paginationSubVendors } from '~/services/hooks/subVendors';
import {
  createEstimateSubVendor,
  updateEstimateSubVendor,
  deleteEstimateSubVendor,
  findAllEstimateSubVendors,
} from '~/services/hooks/estimateSubVendors';
import { updateEstimate, createEstimate } from '~/services/hooks/estimates';
import { paginationEstimateDescriptions } from '~/services/hooks/estimateDescriptions';
import { useSelector } from 'react-redux';
import api from '~/services/api';
import ModalProposal from './modal';
import errorHandler from '~/Utils/errorHandler';
import axios from 'axios';
import ViewTables from './viewTables';


const style = {
  btnNew: {
    col: { marginLeft: 'auto', paddingBottom: '20px' },
    btn: { marginLeft: 'auto' },
  },
  header: {
    col: { paddingBottom: '20px', paddingTop: '10px' },
  },
  footer: {
    col: { paddingTop: '20px' },
  },
};

const estimateInitialValues = {
  id: null,
  sent: null,
  expireTime: 1,
  items: [],
};

export default function EstimatesBasic({ project, customer }) {
  const token = useSelector(state => state.auth.token);
  const projectId = project.id;
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [completeEstimate, setCompleteEstimate] = useState([]);
  const [completeEstimateByArea, setCompleteEstimateByArea] = useState([]);
  const [estimateData, setEstimateData] = useState(estimateInitialValues);
  const [allSubVendors, setAllSubVendors] = useState([]);
  const [selectedEstimateSubVendors, setSelectedEstimateSubVendors] = useState([]);
  const [estimateDescriptions, setEstimateDescriptions] = useState([]);
  const [areas, setAreas] = useState([]);
  const [laborTypes, setLaborTypes] = useState([]);
  const [visible, setVisible] = useState(false);
  const [visibleSubVendors, setVisibleSubVendors] = useState(false);
  const [selectedLaborType, setSelectedLaborType] = useState(null);
  const user = useSelector(state => state.user.profile);
  const [approvedBy, setApprovedBy] = useState(null);
  const [showProposalModal, setProposalModal] = useState(false);


  const fetchData = async () => {
    setLoading(true);
    const data = await paginationEstimates({
      page: 0,
      maxSize: true,
      filters: [{ columnJoin: 'project', field: 'id', value: projectId, restriction: 'EQUAL' }],
    });
    if (data.numberOfElements === 0) {
      const newEstimate = await createEstimate({
        ...estimateInitialValues,
        project: { id: projectId }, 
      });
      setEstimateData(newEstimate);
    } else {
      setEstimateData(data.content[0]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();

    paginationSubVendors({
      page: 0,
      maxSize: true,
      filters: [{ field: 'type', value: 'SUBCONTRACTOR', restriction: 'EQUAL' }],
    }).then(data => {
      setAllSubVendors(data.content);
    });

    paginationAreas({ page: 0, sortBy: 'name', maxSize: true, filters: [] }).then(data => {
      setAreas(data.content);
    });

    paginationLaborTypes({ page: 0, sortBy: 'name', maxSize: true, filters: [] }).then(data => {
      setLaborTypes(data.content);
    });

    paginationEstimateDescriptions({ page: 0, sortBy: 'description', maxSize: true, filters: [] }).then(data => {
      setEstimateDescriptions(data.content);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (estimateData?.id) {
      findAllEstimateSubVendors([
        {
          columnJoin: 'estimateItem',
          columnSecondJoin: 'estimate',
          field: 'id',
          value: estimateData?.id,
          restriction: 'EQUAL',
        },
      ]).then(data => {
        setSelectedEstimateSubVendors(data);
      });
      setApprovedBy(estimateData?.approvedBy);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimateData]);

  const mountEstimate = () => {
    const complete = [];
    laborTypes.forEach(element => {
      element.laborTypeId = element.id;
      element.subVendors = selectedEstimateSubVendors
        .filter(sub => sub.estimateItem?.laborType?.id === element.id)
        .map(sub => sub.subVendor);
      element.subVendors = element.subVendors.filter(
        (sub, index, self) => index === self.findIndex(t => t.id === sub.id)
      );
      element.items = estimateData?.items.filter(item => item?.laborType?.id === element.id);
      complete.push(element);
    });
    const completeFiltered = complete.filter(element => element.items.length > 0);
    setCompleteEstimate(completeFiltered);
  };

  const mountEstimateByArea = () => {
    const complete = [];
    areas.forEach(element => {
      element.area_id = element.id;
      element.items = estimateData?.items.filter(item => item.area.id === element.id);
      complete.push(element);
    });
    const completeFiltered = complete.filter(element => element.items.length > 0);
    setCompleteEstimateByArea(completeFiltered);
  };

  useEffect(() => {
    if (estimateData?.items !== undefined) {
      if (
        estimateData?.items.length > 0 &&
        laborTypes.length > 0 &&
        areas.length > 0 &&
        selectedEstimateSubVendors.length >= 0
      ) {
        mountEstimate();
        mountEstimateByArea();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimateData?.items, laborTypes, areas, selectedEstimateSubVendors]);

  const handleDownloadExcel = async () => {
    axios({
      url: `${process.env.REACT_APP_BACKEND_URL}/estimates/generateEstimatingExcel`,
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      responseType: 'blob',
    }).then(response => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'estimating.xlsx');
      document.body.appendChild(link);
      link.click();
    });
  };

  const handleUploadExcel = async event => {
    setLoading(true);
    try {
      let formData = new FormData();
      formData.append('file', event);
      formData.append('estimateId', estimateData?.id);
      const { data } = await api.post('/estimates/readEstimatingExcel', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (data !== null) {
        setEstimateData(data);
      }

      message.success(t('messages:itemsSuccessImported'));
    } catch (error) {
      setLoading(false);
      errorHandler(error);
    }
    setLoading(false);
  };

  const beforeUploadExcel = file => {
    const isValid = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    if (!isValid) {
      message.error('You can only import file of type: .xlsx');
    }
    const isLt2M = file.size / 1024 / 1024 < 10;
    if (!isLt2M) {
      message.error('The file must be less than 10MB!');
    }
    return isValid && isLt2M;
  };

  const handleNew = () => {
    setSelectedRecord(null);
    setVisible(true);
  };

  const handleEdit = record => {
    setSelectedRecord(record);
    setVisible(true);
  };

  async function removeItems(filterFn) {
    const itensARemover = estimateData?.items.filter(filterFn);
    const subVendorsARemover = selectedEstimateSubVendors.filter(sub =>
      itensARemover.some(i => i.id === sub.estimateItem.id)
    );
    await Promise.all(subVendorsARemover.map(s => deleteEstimateSubVendor(s.id)));
    const novosItems = estimateData?.items.filter(item => !filterFn(item));
    await updateEstimate({ ...estimateData, items: novosItems });
    setEstimateData({ ...estimateData, items: novosItems });
  }

  const handleAddSubVendors = laborTypeId => {
    setVisibleSubVendors(true);
    setSelectedLaborType(laborTypeId);
  };

  const handleCancelSubVendors = () => {
    setVisibleSubVendors(false);
  };

  const tagRender = props => {
    const limitDays = 30;
    const { label, value, closable, onClose } = props;
    const subVendor = allSubVendors.find(sub => sub.id === value);
    const liabilityInsuranceExpirationDate = subVendor?.liabilityInsurence?.expiration_date;
    const workersCompensationExpirationDate = subVendor?.workersCompensationInsurence?.expiration_date;

    // verify if subvendor liabilityInsurance expiration_date is less than limit days
    const isExpiredLiabilityInsurance = liabilityInsuranceExpirationDate
      ? moment(liabilityInsuranceExpirationDate).diff(moment(), 'days') < limitDays
      : true;

    // verify if subvendor workersCompensation expiration_date is less than limit days
    const isExpiredWorkersCompensation = workersCompensationExpirationDate
      ? moment(workersCompensationExpirationDate).diff(moment(), 'days') < limitDays
      : false;

    const color = isExpiredLiabilityInsurance || isExpiredWorkersCompensation ? 'red' : 'default';
    let title = null;

    if (isExpiredLiabilityInsurance || isExpiredWorkersCompensation) {
      title = `Liability Insurence expiration date: ${liabilityInsuranceExpirationDate}\nWorkers Compensation expiration date: ${workersCompensationExpirationDate}`;
    }

    const onPreventMouseDown = event => {
      event.preventDefault();
      event.stopPropagation();
    };
    return (
      <Tag
        color={color}
        key={value}
        title={title}
        onMouseDown={onPreventMouseDown}
        closable={closable}
        onClose={onClose}
        style={{
          marginRight: 3,
        }}
      >
        {label}
      </Tag>
    );
  };

  const handleAddEstimateSubVendor = payload => {
    const exists = selectedEstimateSubVendors.find(
      s => s.estimateItem.id === payload.estimateItem.id && s.subVendor.id === payload.subVendor.id
    );
    if (exists) {
      return updateEstimateSubVendor({ ...payload, id: exists.id });
    } else {
      return createEstimateSubVendor(payload);
    }
  };

  const handleDeleteEstimateSubVendor = () => {
    const allItemsIds = [];
    const allSubVendorsIds = [];
    completeEstimate.forEach(estimateGroup => {
      const estimateItems = estimateGroup.items;
      estimateItems.forEach(item => {
      allItemsIds.push(item.id);
      });
      const estimateSubvendors = estimateGroup.subVendors;
      estimateSubvendors.forEach(subVendor => {
      allSubVendorsIds.push(subVendor.id);
      });
    });
    // filter selectedEstimateSubVendors that are not have estimateItem.id in allItemsIds and not have subvendor.id in allSubVendorsIds
    const estimateSubVendors = selectedEstimateSubVendors.filter(
      sub => !(allItemsIds.includes(sub.estimateItem.id) && allSubVendorsIds.includes(sub.subVendor.id))
    );
    // delete estimateSubVendors
    estimateSubVendors.forEach(async subVendor => {
      await deleteEstimateSubVendor(subVendor.id);
    });
  };

  useEffect(() => {
    if (estimateData?.id && estimateData?.items.length > 0) {
      handleSaveEstimateSubvendors();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimateData?.items]);

  const handleSaveEstimateSubvendors = async status => {
    const promises = [];

    completeEstimate.forEach(group => {
      // percorre todos os subVendors daquele grupo
      group.subVendors.forEach(subVendor => {
        // para cada item daquele grupo
        group.items.forEach(item => {
          const exists = selectedEstimateSubVendors.find(
            ssv => ssv.subVendor.id === subVendor.id && ssv.estimateItem.id === item.id
          );

          const payload = {
            id: exists?.id || null,
            subVendor: subVendor, // objeto completo
            estimateItem: item, // objeto completo
            materialValue: item.materialValue,
            laborValue: item.laborValue,
            quantity: item.copyQtdToVendor ? item.quantity : 0,
            dueDate: estimateData?.expireTime,
            winner: true,
            status: status || 'NEW',
            note: exists?.note || '',
            responserName: exists?.responserName || '',
            responserJobTitle: exists?.responserJobTitle || '',
            value: (item.materialValue + item.laborValue) * (item.copyQtdToVendor ? item.quantity : 0),
          };

          promises.push(handleAddEstimateSubVendor(payload));
        });
      });
    });

    // remove órfãos e aguarda tudo
    promises.push(handleDeleteEstimateSubVendor());
    await Promise.all(promises);

    // recarrega para manter o estado em sincronia
    const refreshed = await findAllEstimateSubVendors([
      {
        columnJoin: 'estimateItem',
        columnSecondJoin: 'estimate',
        field: 'id',
        value: estimateData?.id,
        restriction: 'EQUAL',
      },
    ]);
    setSelectedEstimateSubVendors(refreshed);
  };

  const handleSave = async (values, { setSubmitting }) => {
    setSubmitting(true);
    // 1) Atualiza o estimate principal
    await updateEstimate(values);
    // 2) Sincroniza todos os sub-vendors
    await handleSaveEstimateSubvendors();
    // 3) Recarrega tudo (opcional)
    await fetchData();
    setSubmitting(false);
  };

  const handleApprove = async values => {
    await updateEstimate({ ...estimateData, approvedBy: user, expireTime: values?.expireTime });
    setApprovedBy(user);
    // fetch data again to update estimateData
    fetchData();
    message.info(t('messages:estimates.approved'));
  };

  const handleUnApprove = async () => {
    await updateEstimate({ ...estimateData, approvedBy: null });
    // fetch data again to update estimateData
    fetchData();
    message.info(t('messages:estimates.unapproved'));
  };

  const handleProposalClient = async ({
    model,
    generalConditions,
    overheadAndProfit,
    warehousing,
    notes,
    otherItems,
  }) => {
    setLoading(true);

    try {
      const estimateDTO = {
        estimateId: estimateData?.id,
        model: model,
        generalConditions: generalConditions,
        overheadAndProfit: overheadAndProfit,
        warehousing: warehousing,
        notes: notes,
        otherItems: otherItems,
      };

      await api.post(`/proposalsClient/createProposalClientByEstimate`, estimateDTO);
      // await api.post(`/mail/sendEstimateToClient`, estimateDTO);
      setProposalModal(false);
      message.success(t('messages:estimates.sendToCustomer'));
    } catch (error) {
      errorHandler(error);
    }
    setLoading(false);
  };

  const handleCustomerProposal = async values => {
    await updateEstimate(values);
    fetchData();
    setProposalModal(true);
  };

  const estimateSchema = Yup.object().shape({
    expireTime: Yup.number()
      .min(1)
      .max(300)
      .required(),
  });

  return (
    <Box>
      <Formik
        validateOnBlur={false}
        validateOnChange={false}
        enableReinitialize
        initialValues={estimateData}
        onSubmit={handleSave}
        validationSchema={estimateSchema}
      >
        {({ errors, touched, values, setFieldValue }) => (
          <Form>
            <Row>
              <Col span={12} style={style.header.col}>
                <Space style={{ float: 'left' }}>
                  <>Expires in</>
                  {approvedBy === null ? (
                    <InputNumber name="expireTime" min={1} max={300} style={{ width: 70 }} />
                  ) : (
                    <span style={{ color: 'green' }}>{values.expireTime}</span>
                  )}
                  <>business days after dispatch</>
                  {errors.expireTime && touched.expireTime && <div style={{ color: 'red' }}>({errors.expireTime})</div>}
                </Space>
              </Col>
              <Col span={12} style={style.header.col}>
                <Space style={{ float: 'right' }}>
                  {approvedBy === null ? (
                    <>
                      {estimateData?.items === undefined || estimateData?.items.length === 0 ? (
                        <>
                          <Button onClick={handleDownloadExcel}>
                            <DownloadOutlined />
                            {'Baixar excel'}
                          </Button>

                          <Upload
                            name={'file'}
                            multiple={false}
                            showUploadList={false}
                            accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            beforeUpload={beforeUploadExcel}
                            action={handleUploadExcel}
                          >
                            <Button>
                              <UploadOutlined />
                              {'Importar excel'}
                            </Button>
                          </Upload>
                        </>
                      ) : null}
                      <Button
                        loading={loading}
                        color="primary"
                        onClick={() => {
                          handleNew();
                        }}
                      >
                        <PlusOutlined />
                        {t('screens:estimate.btnNew')}
                      </Button>
                    </>
                  ) : (
                    <span style={{ color: 'green' }}>
                      <AuditOutlined /> This estimate was approved by {approvedBy?.name}
                    </span>
                  )}
                </Space>
              </Col>
            </Row>
            <ViewTables
              loading={loading}
              t={t}
              approvedBy={approvedBy}
              completeEstimate={completeEstimate}
              completeEstimateByArea={completeEstimateByArea}
              allSubVendors={allSubVendors}
              tagRender={tagRender}
              setCompleteEstimate={setCompleteEstimate}
              handleSaveEstimateSubvendors={handleSaveEstimateSubvendors}
              removeItems={removeItems}
              handleEdit={handleEdit}
              handleAddSubVendors={handleAddSubVendors}
            />
            <Row>
              <Col span={24} style={style.footer.col}>
                <Space style={{ float: 'right' }}>
                  {estimateData?.approvedBy === null && user.roles.find(role => role.name === 'ROLE_ADMIN') && (
                    <Popconfirm
                      icon={false}
                      title={t('messages:estimates.confirmApprove')}
                      onConfirm={() => handleApprove(values)}
                      okText={t('messages:yes')}
                      cancelText={t('messages:no')}
                    >
                      <Button loading={loading} type="button" color="primary">
                        <AuditOutlined />
                        {t('screens:estimate.btnApproveEstimate')}
                      </Button>
                    </Popconfirm>
                  )}
                  {estimateData?.approvedBy !== null && user.roles.find(role => role.name === 'ROLE_ADMIN') && (
                    <>
                      <Popconfirm
                        icon={false}
                        title={t('messages:estimates.confirmUnApprove')}
                        onConfirm={() => handleUnApprove()}
                        okText={t('messages:yes')}
                        cancelText={t('messages:no')}
                      >
                        <Button loading={loading} type="button" color="warning">
                          <AuditOutlined />
                          {t('screens:estimate.btnUnApproveEstimate')}
                        </Button>
                      </Popconfirm>
                      <Popconfirm
                        icon={false}
                        title={t('screens:estimate.btnCreateProposalConfirmation')}
                        onConfirm={() => handleCustomerProposal(values)}
                        okText={t('messages:yes')}
                        cancelText={t('messages:no')}
                      >
                        <Button loading={loading} color="primary">
                          {t('screens:estimate.btnCreateCustomerProposal')}
                        </Button>
                      </Popconfirm>
                    </>
                  )}
                </Space>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
      <EstimateForm
        selectedRecord={selectedRecord}
        laborTypes={laborTypes}
        setLaborTypes={setLaborTypes}
        areas={areas}
        setAreas={setAreas}
        estimateData={estimateData}
        setEstimateData={setEstimateData}
        estimateDescriptions={estimateDescriptions}
        setEstimateDescriptions={setEstimateDescriptions}
        visible={visible}
        onClose={() => setVisible(false)}
      />
      <Modal
        title="Subcontractors"
        open={visibleSubVendors}
        onCancel={handleCancelSubVendors}
        width={1000}
        footer={[
          <Button key="back" onClick={handleCancelSubVendors}>
            Close
          </Button>,
        ]}
      >
        <Select
          mode="multiple"
          disabled={approvedBy !== null}
          showArrow
          tagRender={tagRender}
          style={{ width: '100%' }}
          value={
            completeEstimate
              .find(item => item.laborTypeId === selectedLaborType)
              ?.subVendors.map(subVendor => subVendor?.id) || []
          }
          options={allSubVendors
            .filter(subVendor => subVendor.laborTypes.find(laborType => laborType.id === selectedLaborType))
            .map(subVendor => ({
              value: subVendor.id,
              label: `${subVendor.id} - ${subVendor.companyName}`,
            }))}
          onChange={newValue => {
            const newCompleteEstimate = completeEstimate.map(estimate => {
              if (estimate.laborTypeId === selectedLaborType) {
                estimate.subVendors = newValue.map(value => {
                  const subVendor = allSubVendors.find(subVendor => subVendor.id === value);
                  return subVendor;
                });
              }
              return estimate;
            });
            setCompleteEstimate(newCompleteEstimate);
            handleSaveEstimateSubvendors();
          }}
          placeholder="Select Subcontractor..."
          maxTagCount="responsive"
        />
      </Modal>
      <ModalProposal
        record={estimateData}
        visible={showProposalModal}
        loading={loading}
        onSave={handleProposalClient}
        onLoad={() => {
          setLoading(true);
        }}
        onClose={() => {
          setProposalModal(false);
        }}
      />
    </Box>
  );
}
